import { Link } from 'react-router';
import { formatDate, formatMoney, formatNumber } from '@awj/i18n';
import { Skeleton } from '@awj/ui';

import { ExportMenu } from '~/components/ExportMenu.tsx';
import { Icon } from '~/components/Icon.tsx';
import { PageTitle } from '~/components/PageTitle.tsx';
import { useI18n } from '~/i18n/index.tsx';
import { useCoupons } from '~/lib/coupon-queries.ts';
import { couponStanding } from '~/lib/coupon-standing.ts';
import { couponColumns } from '~/lib/export-columns.ts';
import { useRowLink } from '~/lib/row-link.ts';

/**
 * Discount codes.
 *
 * The basket has always accepted codes; this is where they come from. One row per code, with what
 * it takes off, what it asks of the basket, how often it has been used and whether it works today
 * — the four things anyone asks about a code.
 */
export function CouponsPage() {
  const { t, locale } = useI18n();
  const coupons = useCoupons();
  const rowLink = useRowLink();
  const now = Date.now();

  const all = coupons.data ?? [];

  return (
    <>
      <PageTitle
        title={t('nav.coupons')}
        actions={
          <>
            <ExportMenu
              file="discount-codes"
              sheet={t('nav.coupons')}
              columns={couponColumns(t, now)}
              rows={() => all}
              disabled={all.length === 0}
            />
            <Link className="awj-button awj-button--primary awj-button--sm" to="/coupons/new">
              <Icon name="plus" size={17} />
              {t('coupons.new')}
            </Link>
          </>
        }
      />

      {coupons.isPending ? (
        <Skeleton height="260px" />
      ) : all.length === 0 ? (
        <div className="empty-panel">
          <p>{t('coupons.none')}</p>
        </div>
      ) : (
        <div className="data-table">
          <div className="data-table__scroll" tabIndex={0} role="region" aria-label={t('nav.coupons')}>
            <table className="data-table__table">
              <thead>
                <tr>
                  <th className="data-table__th" scope="col">{t('coupons.code')}</th>
                  <th className="data-table__th" scope="col">{t('coupons.discount')}</th>
                  <th className="data-table__th" scope="col">{t('coupons.window')}</th>
                  <th scope="col" className="data-table__th data-table__cell--end">
                    {t('coupons.used')}
                  </th>
                  <th className="data-table__th" scope="col">{t('catalog.status')}</th>
                </tr>
              </thead>
              <tbody>
                {all.map((coupon) => {
                  const state = couponStanding(coupon, now);

                  return (
                    <tr key={coupon.id} {...rowLink(`/coupons/${coupon.id}`)}>
                      <td className="data-table__td">
                        <Link className="data-table__link mono" to={`/coupons/${coupon.id}`}>
                          {coupon.code}
                        </Link>
                      </td>
                      <td className="data-table__td">
                        <bdi>
                          {coupon.type === 'Percentage'
                            ? `${formatNumber(locale, coupon.value)}%`
                            : formatMoney(locale, coupon.value)}
                        </bdi>
                        {coupon.minimumOrderAmount ? (
                          <span className="data-table__sub">
                            {t('coupons.minimumShort', { amount: formatMoney(locale, coupon.minimumOrderAmount) })}
                          </span>
                        ) : null}
                        {coupon.maximumDiscountAmount ? (
                          <span className="data-table__sub">
                            {t('coupons.capShort', { amount: formatMoney(locale, coupon.maximumDiscountAmount) })}
                          </span>
                        ) : null}
                      </td>
                      <td className="data-table__td">
                        {coupon.startsAt || coupon.expiresAt ? (
                          <>
                            {coupon.startsAt ? (
                              <span className="data-table__sub">
                                {t('coupons.fromDate', { date: formatDate(locale, coupon.startsAt) })}
                              </span>
                            ) : null}
                            {coupon.expiresAt ? (
                              <span className="data-table__sub">
                                {t('coupons.untilDate', { date: formatDate(locale, coupon.expiresAt) })}
                              </span>
                            ) : null}
                          </>
                        ) : (
                          <span className="data-table__sub">{t('coupons.noEnd')}</span>
                        )}
                      </td>
                      <td className="data-table__td data-table__cell--end">
                        <bdi>
                          {coupon.usageLimit != null
                            ? t('coupons.usedOf', {
                                used: formatNumber(locale, coupon.timesUsed),
                                limit: formatNumber(locale, coupon.usageLimit),
                              })
                            : formatNumber(locale, coupon.timesUsed)}
                        </bdi>
                      </td>
                      <td className="data-table__td">
                        <span className={`status status--${state.tone}`}>{t(state.label)}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
